import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { api } from "@jourfx/convex/client";
import { Button } from "@jourfx/ui";

import { PageHeader } from "../../components/PageHeader";
import {
  buildAreaDefaults,
  buildImportPreview,
  type CsvUserRow,
  type ImportPreviewRow,
  parseUsersCsv,
  splitBusinessAreas,
} from "./importUsers";

type Id<T extends string> = string & { __tableName: T };

type AvailableLicense = {
  _id: string;
  kind: "base" | string;
  moduleId?: string | null;
  seats?: number | null;
  assignedCount: number;
};

type Organization = {
  _id: string;
  name: string;
  slug?: string;
};

type ExistingUser = {
  email: string;
};

type ImportResult = {
  lineNumber: number;
  email: string;
  name: string;
  status: "success" | "error" | "skipped";
  message: string;
};

function getLicenseLabel(
  license: AvailableLicense,
  t: TFunction<"users">
) {
  if (license.kind === "base") {
    return t("import.baseLicense", "Base License");
  }

  const moduleLabel = t("import.moduleLicense", "Module License");
  return license.moduleId ? `${moduleLabel} (${license.moduleId})` : moduleLabel;
}

function getPreviewReasonLabel(
  row: ImportPreviewRow,
  t: TFunction<"users">
) {
  if (!row.reason) {
    return row.status === "ready"
      ? t("import.ready", "Ready")
      : t("import.invalid", "Invalid");
  }

  switch (row.reason) {
    case "duplicate_email":
      return t("import.reason.duplicate_email", "This email appears multiple times in the file.");
    case "existing_email":
      return t("import.reason.existing_email", "A user with this email address already exists.");
    case "invalid_email":
      return t("import.reason.invalid_email", "The email address is invalid.");
    case "invalid_name":
      return t("import.reason.invalid_name", "The name could not be split into first and last name.");
    case "missing_area":
      return t("import.reason.missing_area", "No business area was provided.");
    case "missing_area_mapping":
      return t(
        "import.reason.missing_area_mapping",
        "No business unit mapping is selected for this business area."
      );
    case "multiple_areas":
      return t(
        "import.reason.multiple_areas",
        "Rows with multiple business areas must be imported manually."
      );
    case "unsupported_role":
      return t("import.reason.unsupported_role", "Admin rows are not supported in bulk import.");
    default:
      return t("import.invalid", "Invalid");
  }
}

export function UserImport() {
  const { t } = useTranslation("users");
  const apiRef = api as any;

  const [csvRows, setCsvRows] = useState<CsvUserRow[]>([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  const [areaAssignments, setAreaAssignments] = useState<Record<string, string>>({});
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);

  const createUser = useMutation(api.admin.users.create);
  const orgsResult = useQuery(api.admin.organizations.list, {});
  const organizations = (orgsResult?.organizations ?? []) as Organization[];
  const existingUsers = (useQuery(api.admin.users.list, { limit: 500 }) ?? []) as ExistingUser[];
  const licensesResult = useQuery(apiRef.admin.licenseAssignments.listAvailable, {});
  const licenseEntries = (licensesResult?.licenses ?? []) as AvailableLicense[];

  const sourceAreas = useMemo(() => {
    return Array.from(
      new Set(
        csvRows.flatMap((row) => splitBusinessAreas(row.operationalArea))
      )
    );
  }, [csvRows]);

  useEffect(() => {
    if (csvRows.length === 0 || organizations.length === 0) {
      return;
    }

    const defaults = buildAreaDefaults(csvRows, organizations);
    setAreaAssignments((current) => {
      const next = { ...current };
      let changed = false;
      for (const [area, organizationId] of Object.entries(defaults)) {
        if (!next[area]) {
          next[area] = organizationId;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [csvRows, organizations]);

  const existingEmails = useMemo(
    () => new Set(existingUsers.map((user) => user.email.trim().toLowerCase())),
    [existingUsers]
  );

  const previewRows = useMemo(() => {
    return buildImportPreview({
      rows: csvRows,
      organizations,
      existingEmails,
      areaAssignments,
    });
  }, [areaAssignments, csvRows, existingEmails, organizations]);

  const readyRows = previewRows.filter((row) => row.status === "ready");
  const invalidRows = previewRows.filter((row) => row.status === "invalid");
  const skippedRows = previewRows.filter((row) => row.status === "skipped");

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const csvText = await file.text();
      const parsedRows = parseUsersCsv(csvText);
      setCsvRows(parsedRows);
      setCsvFileName(file.name);
      setCsvError(null);
      setImportResults([]);
      setAreaAssignments({});
    } catch (error) {
      setCsvRows([]);
      setCsvFileName(file.name);
      setImportResults([]);
      setAreaAssignments({});
      setCsvError(error instanceof Error ? error.message : t("import.invalid"));
    }
  };

  const toggleLicense = (licenseId: string, assigned: boolean) => {
    setSelectedLicenseIds((current) => {
      if (assigned) {
        return current.includes(licenseId) ? current : [...current, licenseId];
      }
      return current.filter((id) => id !== licenseId);
    });
  };

  const handleImport = async () => {
    if (readyRows.length === 0 || selectedLicenseIds.length === 0) {
      return;
    }

    setIsImporting(true);
    const nextResults: ImportResult[] = previewRows
      .filter((row) => row.status !== "ready")
      .map((row) => ({
        lineNumber: row.lineNumber,
        email: row.email,
        name: row.name,
        status: "skipped",
        message: getPreviewReasonLabel(row, t),
      }));

    try {
      for (const row of readyRows) {
        try {
          await createUser({
            organizationId: row.organizationId as Id<"organizations">,
            email: row.email,
            firstName: row.firstName,
            lastName: row.lastName,
            role: row.role as "gutachter" | "assistent",
            phone: undefined,
            sendInvite: true,
            licenseIds: selectedLicenseIds as Id<"licenses">[],
          });

          nextResults.push({
            lineNumber: row.lineNumber,
            email: row.email,
            name: row.name,
            status: "success",
            message: t("import.results.inviteQueued", "Invitation queued"),
          });
        } catch (error) {
          nextResults.push({
            lineNumber: row.lineNumber,
            email: row.email,
            name: row.name,
            status: "error",
            message: error instanceof Error ? error.message : t("import.invalid"),
          });
        }

        setImportResults([...nextResults].sort((a, b) => a.lineNumber - b.lineNumber));
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("import.title")}
        description={t("import.subtitle")}
        actions={
          <Button asChild variant="outline">
            <Link to="/users">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("import.back")}
            </Link>
          </Button>
        }
      />

      <section className="rounded-lg border bg-card p-6">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="space-y-3">
            <div>
              <label htmlFor="csv-file" className="block text-sm font-medium text-foreground">
                {t("import.uploadLabel")}
              </label>
              <p className="text-sm text-muted-foreground">{t("import.uploadHint")}</p>
            </div>
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-foreground"
            />
            {csvFileName ? (
              <p className="text-sm text-muted-foreground">
                {t("import.fileName")}: <span className="font-medium text-foreground">{csvFileName}</span>
              </p>
            ) : null}
            {csvError ? (
              <p className="text-sm text-destructive">{csvError}</p>
            ) : null}
          </div>
        </div>
      </section>

      {csvRows.length > 0 ? (
        <>
          <section className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">{t("import.areasTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("import.areasSubtitle")}</p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {sourceAreas.map((area) => (
                <div key={area} className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t("import.sourceArea")}: {area}
                  </label>
                  <p className="text-xs text-muted-foreground">{t("import.targetOrganization")}</p>
                  <select
                    value={areaAssignments[area] ?? ""}
                    onChange={(event) =>
                      setAreaAssignments((current) => ({
                        ...current,
                        [area]: event.target.value,
                      }))
                    }
                    className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">{t("create.organization")}</option>
                    {organizations.map((organization) => (
                      <option key={organization._id} value={organization._id}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground">{t("import.licensesTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("import.licensesSubtitle")}</p>

            <div className="mt-4 space-y-3">
              {licenseEntries.map((license) => {
                const isAssigned = selectedLicenseIds.includes(license._id);
                return (
                  <label
                    key={license._id}
                    className="flex items-center justify-between rounded-lg border px-4 py-3"
                  >
                    <div>
                      <p className="font-medium text-foreground">{getLicenseLabel(license, t)}</p>
                      <p className="text-sm text-muted-foreground">
                        {t("create.seats")}: {license.assignedCount}/{license.seats ?? "?"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={(event) => toggleLicense(license._id, event.target.checked)}
                        aria-label={t("import.assigned")}
                      />
                      <span className="text-sm text-foreground">{t("import.assigned")}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="rounded-lg border bg-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("import.previewTitle")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("import.previewSubtitle")}</p>
              </div>
              <Button
                onClick={handleImport}
                disabled={readyRows.length === 0 || selectedLicenseIds.length === 0 || isImporting}
              >
                {isImporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {isImporting
                  ? t("import.importing")
                  : t("import.importAction", {
                      count: readyRows.length,
                      defaultValue:
                        readyRows.length === 1
                          ? "Import {{count}} user"
                          : "Import {{count}} users",
                    })}
              </Button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">{t("import.summary.total")}</p>
                <p className="text-2xl font-semibold text-foreground">{previewRows.length}</p>
              </div>
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">{t("import.summary.ready")}</p>
                <p className="text-2xl font-semibold text-foreground">{readyRows.length}</p>
              </div>
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">{t("import.summary.invalid")}</p>
                <p className="text-2xl font-semibold text-foreground">{invalidRows.length}</p>
              </div>
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">{t("import.summary.skipped")}</p>
                <p className="text-2xl font-semibold text-foreground">{skippedRows.length}</p>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">{t("import.columns.name")}</th>
                    <th className="px-3 py-2">{t("import.columns.email")}</th>
                    <th className="px-3 py-2">{t("import.columns.role")}</th>
                    <th className="px-3 py-2">{t("import.columns.organization")}</th>
                    <th className="px-3 py-2">{t("import.columns.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => (
                    <tr key={`${row.lineNumber}-${row.email}`} className="border-b align-top last:border-b-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground">{row.name}</div>
                        <div className="text-xs text-muted-foreground">#{row.lineNumber}</div>
                      </td>
                      <td className="px-3 py-2 text-foreground">{row.email}</td>
                      <td className="px-3 py-2 text-foreground">
                        {row.role ? t(`roles.${row.role}`) : row.roleLabel}
                      </td>
                      <td className="px-3 py-2 text-foreground">
                        {row.organizationName ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground">
                          {row.status === "ready"
                            ? t("import.ready")
                            : row.status === "skipped"
                              ? t("import.skipped")
                              : t("import.invalid")}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {getPreviewReasonLabel(row, t)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {importResults.length > 0 ? (
        <section className="rounded-lg border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">{t("import.resultsTitle")}</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t("import.columns.name")}</th>
                  <th className="px-3 py-2">{t("import.columns.email")}</th>
                  <th className="px-3 py-2">{t("import.columns.status")}</th>
                </tr>
              </thead>
              <tbody>
                {importResults.map((result) => (
                  <tr key={`${result.lineNumber}-${result.email}`} className="border-b last:border-b-0">
                    <td className="px-3 py-2 text-foreground">{result.name}</td>
                    <td className="px-3 py-2 text-foreground">{result.email}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-foreground">
                        {result.status === "success"
                          ? t("import.results.success")
                          : result.status === "error"
                            ? t("import.results.error")
                            : t("import.skipped")}
                      </div>
                      <div className="text-xs text-muted-foreground">{result.message}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
